import type { MouseEvent, PointerEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';

import { analytics } from '@/analytics/core/analytics';
import { resolveButtonTracking } from '@/analytics/core/resolveButtonTracking';
import Done from '@/assets/icons/operational-dashboard/done.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import { useMenuContext } from './MenuContext';

import styles from './Menu.module.scss';

export interface MenuItemProps {
    children: ReactNode;
    /** Runs before the item closes the menu. */
    onClick?: (event: MouseEvent<HTMLElement>) => void;
    disabled?: boolean;
    /** Marks the item for the page or choice the reader is on. */
    active?: boolean;
    /** Leaves the menu open after the click, for items that change something in place. */
    keepOpen?: boolean;
    /** A destructive action. */
    danger?: boolean;
    icon?: ReactNode;
    /** Router path; renders the item as a link. */
    to?: string;
    /** Renders the row as a `div`, e.g. one holding a switch with its own control. */
    as?: 'div';
    title?: string;
    className?: string;
    'data-testid'?: string;
    /** Analytics id, reported as a button click like a Button's, for an item that acts. */
    trackingId?: string;
}

/**
 * Hovering an item highlights it without moving focus, as the menu always has. Radix
 * would focus the hovered item, which took focus out of a search field in the menu and
 * sent the next letter typed to the item list instead.
 */
function keepFocusOnHover(event: PointerEvent<HTMLElement>): void {
    event.preventDefault();
}

function keepOpenOnSelect(event: Event): void {
    event.preventDefault();
}

/** An action in a Menu. */
export function MenuItem(props: MenuItemProps) {
    const {
        children,
        onClick,
        disabled = false,
        active = false,
        keepOpen = false,
        danger = false,
        icon,
        to,
        as,
        className,
    } = props;
    const menu = useMenuContext('MenuItem');

    // The item unmounts with the menu as it closes, so a dialog it opens would find nothing
    // focused to hand focus back to. The trigger takes focus first and stands in for it.
    const handleClick = (event: MouseEvent<HTMLElement>) => {
        if (!keepOpen) menu.triggerRef.current?.focus({ preventScroll: true });
        const tracking = props.trackingId
            ? resolveButtonTracking({ trackingId: props.trackingId })
            : null;
        if (tracking) {
            analytics.buttonClicked?.({ ...tracking, buttonVariant: 'menu_item' });
        }
        onClick?.(event);
    };

    const content = (
        <>
            {icon ? (
                <span className={styles.itemIcon} aria-hidden="true">
                    {icon}
                </span>
            ) : null}
            {children}
        </>
    );
    const itemClassName = classNames(
        styles.item,
        { [styles.itemActive]: active, [styles.itemDanger]: danger },
        className
    );
    const shared = {
        title: props.title,
        className: itemClassName,
        'data-testid': props['data-testid'],
        'data-active': active ? '' : undefined,
    };

    return (
        <DropdownMenu.Item
            asChild
            disabled={disabled}
            onClick={handleClick}
            onSelect={keepOpen ? keepOpenOnSelect : undefined}
            onPointerMove={keepFocusOnHover}
            onPointerLeave={keepFocusOnHover}>
            {to !== undefined ? (
                <Link to={to} {...shared} aria-current={active ? 'page' : undefined}>
                    {content}
                </Link>
            ) : as === 'div' ? (
                <div {...shared}>{content}</div>
            ) : (
                <button type="button" {...shared} disabled={disabled}>
                    {content}
                </button>
            )}
        </DropdownMenu.Item>
    );
}

interface CheckableItemProps {
    children: ReactNode;
    checked: boolean;
    disabled?: boolean;
    className?: string;
    title?: string;
    /** Replaces the visible label as the accessible name, e.g. to add why it is disabled. */
    'aria-label'?: string;
}

export interface MenuCheckboxItemProps extends CheckableItemProps {
    onCheckedChange: (checked: boolean, event: MouseEvent<HTMLButtonElement>) => void;
}

function CheckMark(props: { checked: boolean; shape: 'box' | 'dot' }) {
    const { checked, shape } = props;
    return (
        <span
            className={classNames(styles.check, styles[shape], { [styles.checked]: checked })}
            aria-hidden="true">
            {checked && shape === 'box' ? (
                <SvgIcon icon={Done} size="custom" customSize={12} color="currentColor" />
            ) : null}
        </span>
    );
}

/** One of several things that can be on at once. The menu stays open. */
export function MenuCheckboxItem(props: MenuCheckboxItemProps) {
    const { children, checked, disabled = false, onCheckedChange, className } = props;

    return (
        <DropdownMenu.Item
            asChild
            disabled={disabled}
            role="menuitemcheckbox"
            aria-checked={checked}
            onSelect={keepOpenOnSelect}
            onPointerMove={keepFocusOnHover}
            onPointerLeave={keepFocusOnHover}>
            <button
                type="button"
                disabled={disabled}
                title={props.title}
                aria-label={props['aria-label']}
                className={classNames(styles.item, className)}
                onClick={(event) => onCheckedChange(!checked, event)}>
                <CheckMark checked={checked} shape="box" />
                <span className={styles.itemLabel}>{children}</span>
            </button>
        </DropdownMenu.Item>
    );
}

export interface MenuRadioItemProps extends CheckableItemProps {
    onSelect: (event: MouseEvent<HTMLButtonElement>) => void;
    /** Leaves the menu open after choosing. */
    keepOpen?: boolean;
    /** The radio mark. Off, the current choice is only the highlighted row. */
    indicator?: boolean;
    /** Solid accent fill for the current choice. The default is the faint accent wash. */
    solid?: boolean;
}

/** One choice out of the menu's options. Choosing closes the menu. */
export function MenuRadioItem(props: MenuRadioItemProps) {
    const {
        children,
        checked,
        disabled = false,
        onSelect,
        keepOpen = false,
        className,
        indicator = true,
        solid = false,
    } = props;

    return (
        <DropdownMenu.Item
            asChild
            disabled={disabled}
            role="menuitemradio"
            aria-checked={checked}
            onSelect={keepOpen ? keepOpenOnSelect : undefined}
            onPointerMove={keepFocusOnHover}
            onPointerLeave={keepFocusOnHover}>
            <button
                type="button"
                disabled={disabled}
                title={props.title}
                aria-label={props['aria-label']}
                className={classNames(
                    styles.item,
                    { [styles.itemActive]: checked, [styles.itemSolid]: checked && solid },
                    className
                )}
                onClick={onSelect}>
                {indicator ? <CheckMark checked={checked} shape="dot" /> : null}
                <span className={styles.itemLabel}>{children}</span>
            </button>
        </DropdownMenu.Item>
    );
}

/** A label above a group of items. */
export function MenuHeader(props: { children: ReactNode; className?: string }) {
    return <h6 className={classNames(styles.header, props.className)}>{props.children}</h6>;
}

export function MenuDivider() {
    return <hr className={styles.divider} />;
}
